import {
  DisplayStatus as PaymentActivityStatus,
  FilterQueryType,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { DisplayStatus as BulkStatus } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { DisplayStatus as TemplateStatus } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";

export enum FilterChips {
  PARENT = "parent",
  STATUS = "status",
  ACCOUNT = "account",
  AMOUNT = "amount",
  DATE = "date",
  RECURRING_PAYMENT = "recurringPayment",
  BULK_PAYMENT = "bulkPayment",
  FREQUENCY = "frequency",
  BULK_STATUS = "bulkStatus",
  METHOD = "method",
  ACTION = "action",
  TEMPLATE_STATUS = "templateStatus",
  TEMPLATE = "template",
  WIRE_IMPORT_BATCH = "wireImportBatch",
  FILE_IMPORT_METHOD = "fileImportMethod",
}

export const FILTER_CONFIG = {
  [FilterChips.DATE]: {
    type: "range",
    labelKey: "label-date-range",
    queryKeys: {
      min: "scheduled_date_after",
      max: "scheduled_date_before",
    },
  },
  [FilterChips.STATUS]: {
    type: "multi-select",
    queryKey: "state_in",
    labelKey: "payment-activity-label-status",
    options: [
      { label: PaymentActivityStatus.completed, key: "completed" },
      {
        label: PaymentActivityStatus.pending_approval,
        key: "pending_approval",
      },
      { label: PaymentActivityStatus.scheduled, key: "scheduled" },
      { label: PaymentActivityStatus.processing, key: "processing" },

      { label: PaymentActivityStatus.in_progress, key: "in_progress" },

      { label: PaymentActivityStatus.failed, key: "failed" },
      { label: PaymentActivityStatus.canceled, key: "canceled" },
    ],
  },
  [FilterChips.BULK_STATUS]: {
    type: "multi-select",
    queryKey: "state_in",
    labelKey: "payment-activity-label-status",
    options: [
      { label: BulkStatus.completed, key: "completed" },
      {
        label: BulkStatus.partially_completed,
        key: "partially_completed",
      },
      {
        label: BulkStatus.awaiting_approval,
        key: "awaiting_approval",
      },
      { label: BulkStatus.in_progress, key: "in_progress" },

      { label: BulkStatus.scheduled, key: "scheduled" },

      { label: BulkStatus.failed, key: "failed" },
      {
        label: BulkStatus.bulk_payment_canceled,
        key: "bulk_payment_canceled",
      },
    ],
  },
  [FilterChips.AMOUNT]: {
    type: "range",
    labelKey: "label-amount",
    queryKeys: {
      min: "amount_gte",
      max: "amount_lte",
    },
  },
  [FilterChips.ACCOUNT]: {
    type: "multi-select",
    queryKey: "institution_account_in",
    labelKey: "label-from-account",
  },
  [FilterChips.FREQUENCY]: {
    type: "multi-select",
    queryKey: "frequency",
    labelKey: "label-frequency",
    options: [
      { label: "One-time", key: "one_time" },
      { label: "Daily", key: "daily" },
      { label: "Weekly", key: "weekly" },
      { label: "Every 2 weeks", key: "every_two_weeks" },
      { label: "1st and 15th", key: "first_and_fifteenth" },
      { label: "Monthly", key: "monthly" },
      { label: "Yearly", key: "yearly" },
    ],
  },
  [FilterChips.RECURRING_PAYMENT]: {
    type: "static",
    labelKey: "recurring-payment-filter-label",
    queryKey: "parent",
  },
  [FilterChips.BULK_PAYMENT]: {
    type: "static",
    labelKey: "bulk-payment-filter-label",
    queryKey: "bulk_payment",
  },
  [FilterChips.WIRE_IMPORT_BATCH]: {
    type: "static",
    labelKey: "wire-file-import-method-name",
    queryKey: "wire_import_batch",
  },
  [FilterChips.METHOD]: {
    type: "multi-select",
    queryKey: "payment_type_in",
    labelKey: "heading-wire-method", // "Method" label reused
    options: [
      {
        label: "ACH",
        key: "ach",
        featureFlags: ["ach_payments", "ach_payments_collect"],
      },
      {
        label: "Instant payment",
        key: "fednow",
        featureFlags: ["fednow_send", "fednow_receive"],
      },
      {
        label: "Domestic wire",
        key: "domestic_wire",
        featureFlags: ["wires"],
      },
      {
        label: "International wire",
        key: "international_wire",
        featureFlags: ["international_wires"],
      },
    ],
  },
  [FilterChips.FILE_IMPORT_METHOD]: {
    type: "multi-select",
    queryKey: "type_in",
    labelKey: "heading-wire-method",
    options: [
      {
        label: "ACH",
        key: "ach",
        featureFlags: ["ach_payments", "bulk_payments", "ach_file_import"],
      },
      {
        label: "Wire",
        key: "wire",
        featureFlags: ["wires", "domestic_wire_file_import"],
      },
    ],
  },
  [FilterChips.ACTION]: {
    type: "multi-select",
    queryKey: "direction_in",
    labelKey: "label-action",
    options: [
      { label: "Collect", key: "debit" },
      {
        label: "Send",
        key: "credit",
      },
      // TODO: confirm when backend is ready to support these filters for reversals
      {
        label: "Reverse collect",
        key: "debit_reversal",
        featureFlags: ["user_initiated_ach_reversals"],
      },
      {
        label: "Reverse send",
        key: "credit_reversal",
        featureFlags: ["user_initiated_ach_reversals"],
      },
    ],
  },
  [FilterChips.TEMPLATE_STATUS]: {
    type: "multi-select",
    queryKey: "state_in",
    labelKey: "payment-activity-label-status",
    options: [
      { label: TemplateStatus.active, key: "active" },
      { label: TemplateStatus.active_pending, key: "active_pending" },
      { label: TemplateStatus.pending_approval, key: "pending_approval" },
      { label: TemplateStatus.archived, key: "archived" },
    ],
  },
  [FilterChips.TEMPLATE]: {
    type: "multi-select",
    queryKey: "template_version",
    labelKey: "payment-activity-filter-templates",
  },
} as const;

export type FilterChipsType = keyof typeof FILTER_CONFIG;

export type StaticFilterType = {
  isActive: boolean;
  handleSetActive: (filterName: FilterChips | "") => void;
  handleToggleFilter: (filterName: FilterChipsType) => void;
};

export type FilterType = {
  filters: FilterQueryType;
  handleSetFilter: (values: FilterQueryType) => void;
} & StaticFilterType;
